import { create } from "zustand";
import useBuyFormStore from "./useBuyFormStore";
import { shallow } from "zustand/shallow";
import { IBuyUIState } from "types/stores.type";
import useParkingInfoStore from "./useParkingInfoStore";

// Funguje pouze pro Blokové čištění, ostatní chybové hlášky jsou nekonzistetntní a uživatelsky nečitelný
const getApiErrorContent = (error?: unknown): { key: string; params: Record<string, string> } | null => {
    if (!(error instanceof Error) || !error.message) {
        return null;
    }

    const normalizedMessage = error.message.replace(/^Api Error:\s*/, "").trim();
    const jsonStartIndex = normalizedMessage.indexOf("{");

    if (jsonStartIndex >= 0) {
        try {
            const parsedMessage = JSON.parse(normalizedMessage.slice(jsonStartIndex));
            if (
                parsedMessage?.title === "BadRequest" &&
                typeof parsedMessage?.detail === "string" &&
                parsedMessage.detail.startsWith("Blokové čištění ")
            ) {
                const blockCleaningMatch = parsedMessage.detail.match(
                    /^Blokové čištění\s+(\d{2}\.\d{2}\.\d{4})\s+od\s+(\d{2}:\d{2})\s+do\s+(\d{2}:\d{2})$/
                );

                if (blockCleaningMatch) {
                    return {
                        key: "buy.blockCleaningError",
                        params: {
                            date: blockCleaningMatch[1],
                            from: blockCleaningMatch[2],
                            to: blockCleaningMatch[3],
                        },
                    };
                }
            }
        } catch {
            return null;
        }
    }

    return null;
};

const useBuyUIStore = create<IBuyUIState>()((set, get) => ({
    showLoader: false,
    isSettingsLoaded: false,
    checkedLatestValues: false,
    selectCard: false,
    showNoEmailWarning: false,
    isSpecialOption: false,
    latestValues: false,
    hasApiError: false,
    apiErrorMessage: null,
    apiErrorMessageParams: null,
    disablePaymentButtons: true,
    disableLicencePlateInput: false,
    disablePaymentButtonsDueToLimitExceeded: false,

    setShowLoader: (showLoader) => set({ showLoader }),
    setIsSettingsLoaded: (isSettingsLoaded) => set({ isSettingsLoaded }),
    setCheckedLatestValues: (checkedLatestValues) => set({ checkedLatestValues }),
    setSelectCard: (selectCard) => set({ selectCard }),
    setShowNoEmailWarning: (showNoEmailWarning) => set({ showNoEmailWarning }),
    setIsSpecialOption: (isSpecialOption) => set({ isSpecialOption }),
    setLatestValues: (latestValues) => set({ latestValues }),
    setHasApiError: (hasApiError) => set({ hasApiError }),
    setApiErrorMessage: (apiErrorMessage) => set({ apiErrorMessage }),
    setApiErrorMessageParams: (apiErrorMessageParams) => set({ apiErrorMessageParams }),
    setDisablePaymentButtons: (disablePaymentButtons) => set({ disablePaymentButtons }),
    setDisableLicencePlateInput: (disable) => set({ disableLicencePlateInput: disable }),
    setDisablePaymentButtonsDueToLimitExceeded: (disable) => set({ disablePaymentButtonsDueToLimitExceeded: disable }),

    resetPaymentFlow: () => {
        set({
            showLoader: false,
            selectCard: false,
            showNoEmailWarning: false,
            disablePaymentButtons: false,
            apiErrorMessage: null,
            apiErrorMessageParams: null,
        });
    },

    handleApiError: (error) => {
        const apiErrorContent = getApiErrorContent(error);

        set({
            hasApiError: true,
            apiErrorMessage: apiErrorContent?.key ?? null,
            apiErrorMessageParams: apiErrorContent?.params ?? null,
            disablePaymentButtons: false,
            showLoader: false,
            selectCard: false,
        });

        setTimeout(() => {
            set({ hasApiError: false, apiErrorMessage: null, apiErrorMessageParams: null });
        }, 10000);
    },

    initSubscriptions: () => {
        const { setDisablePaymentButtons, setDisableLicencePlateInput } = useBuyUIStore.getState();
        const unsubscribers: (() => void)[] = [];

        unsubscribers.push(
            useBuyFormStore.subscribe(
                (state) => ({ licencePlate: state.licencePlate }),
                ({ licencePlate }) => {
                    setDisablePaymentButtons(!licencePlate);
                },
                { equalityFn: shallow, fireImmediately: true }
            )
        );

        unsubscribers.push(
            useParkingInfoStore.subscribe(
                (state) => ({ currentSession: state.currentSession }),
                ({ currentSession }) => {
                    !currentSession && setDisableLicencePlateInput(false);
                    currentSession && setDisableLicencePlateInput(true);
                },
                { equalityFn: shallow, fireImmediately: true }
            )
        );

        (window as any).__buyUIStoreUnsubscribe = () => {
            unsubscribers.forEach((unsub) => unsub());
        };
    },
}));

export default useBuyUIStore;
